import { Search, Plus, ChevronRight } from 'lucide-react'
import { useCatalog, useCurrency, useStoreContent, useProductLink, price, isAvailable, productImage } from '../shared'
import StockBadge from '../../../../components/StockBadge'
import { t } from '../../../../i18n'

const PASTELS = ['#fde2f3', '#fef3c7', '#d1fae5', '#dbeafe', '#ede9fe', '#ffedd5']

// Playful "shelves": round category bubbles up top, then every category is
// its own horizontally scrolling row of colorful cards (like a streaming
// app). Searching collapses the shelves into one results grid.
export default function StoreVibrantTemplate({ menu, addItem, storeName }) {
  const { products, filtered, categories, search, setSearch } = useCatalog(menu)
  const currency = useCurrency()
  const open = useProductLink()
  const content = useStoreContent({ storeName, title: 'Little things that make you smile.', subtitle: 'Fresh finds, bright colors, happy prices.' })
  const searching = search.trim().length > 0

  const card = (item, i) => (
    <article key={item.id} className="snap-start shrink-0 w-[190px] sm:w-[220px] rounded-[28px] p-3 flex flex-col" style={{ background: PASTELS[i % PASTELS.length] }}>
      <button onClick={() => open(item)} className="aspect-square rounded-[22px] bg-white/70 overflow-hidden grid place-items-center" aria-label={t("View {name}", { name: item.name })}>
        {productImage(item)
          ? <img loading="lazy" src={productImage(item)} alt={item.name} className="w-full h-full object-cover hover:scale-105 transition-transform" />
          : <span className="text-4xl">🛍️</span>}
      </button>
      <button onClick={() => open(item)} className="font-extrabold text-slate-900 mt-3 text-start leading-tight line-clamp-2 min-h-[2.5em]">{item.name}</button>
      <StockBadge item={item} className="self-start mt-1" />
      <div className="flex items-center justify-between mt-auto pt-3">
        <span className="font-black text-slate-900">{price(item.price, currency)}</span>
        <button disabled={!isAvailable(item)} onClick={() => addItem(item)} aria-label={t("Add {name} to cart", { name: item.name })}
          className="w-10 h-10 rounded-full bg-slate-900 text-white grid place-items-center hover:scale-110 transition-transform disabled:opacity-30">
          <Plus size={18} />
        </button>
      </div>
    </article>
  )

  return (
    <div className="vibrant-shelves" style={{ fontFamily: "'Trebuchet MS', 'Segoe UI', sans-serif" }}>
      <section className="relative overflow-hidden rounded-[36px] px-6 py-14 md:py-20 text-center mb-10" style={{ background: 'linear-gradient(135deg,#fbcfe8 0%,#ddd6fe 50%,#bae6fd 100%)' }}>
        <span className="absolute -top-16 -start-10 w-56 h-56 rounded-full bg-yellow-200/70 blur-2xl" aria-hidden="true" />
        <span className="absolute -bottom-20 -end-10 w-64 h-64 rounded-full bg-pink-300/60 blur-2xl" aria-hidden="true" />
        {content.heroImage && <img src={content.heroImage} alt="" className="relative mx-auto w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg mb-6" />}
        <p className="relative inline-block bg-white/70 rounded-full px-4 py-1 text-xs font-bold tracking-wider text-fuchsia-700">{content.name}</p>
        <h1 className="relative text-4xl md:text-6xl font-black text-slate-900 mt-5 max-w-3xl mx-auto leading-tight">{content.heroTitle}</h1>
        <p className="relative text-slate-700 mt-4 max-w-xl mx-auto">{content.heroSubtitle}</p>
        <label className="relative mt-8 mx-auto max-w-md flex items-center gap-3 bg-white rounded-full px-5 py-3.5 shadow-lg">
          <Search size={18} className="text-fuchsia-500" />
          <input aria-label={t("Search products")} value={search} onChange={e => setSearch(e.target.value)} placeholder={t("What are you looking for?")} className="flex-1 outline-none bg-transparent" />
        </label>
      </section>

      {!searching && categories.length > 1 && (
        <nav className="flex gap-5 overflow-x-auto no-scrollbar pb-4 mb-8" aria-label={t("Categories")}>
          {categories.map((c, i) => {
            const img = productImage((c.items || []).find(productImage) || {})
            return (
              <a key={c.id} href={`#shelf-${c.id}`} className="shrink-0 flex flex-col items-center gap-2 w-20">
                <span className="w-20 h-20 rounded-full overflow-hidden grid place-items-center ring-4 ring-white shadow-md" style={{ background: PASTELS[i % PASTELS.length] }}>
                  {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl">✨</span>}
                </span>
                <span className="text-xs font-bold text-slate-700 text-center line-clamp-2">{c.name}</span>
              </a>
            )
          })}
        </nav>
      )}

      {searching ? (
        filtered.length ? (
          <>
            <p className="font-extrabold text-slate-900 mb-4">{filtered.length} {t("result")}{filtered.length === 1 ? '' : 's'} {t("for “")}{search}”</p>
            <div className="flex flex-wrap gap-4">{filtered.map((item, i) => card(item, i))}</div>
          </>
        ) : <p className="text-center text-slate-500 py-16 font-bold">{t("No matches - try another word 💭")}</p>
      ) : products.length === 0 ? (
        <p className="text-center text-slate-500 py-16 font-bold">{t("New goodies are coming soon ✨")}</p>
      ) : (
        categories.map((c, ci) => (
          <section key={c.id} id={`shelf-${c.id}`} className="mb-12 scroll-mt-24">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                <span className="w-3 h-3 rounded-full" style={{ background: ['#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'][ci % 5] }} />
                {c.name}
              </h2>
              <span className="text-sm font-bold text-slate-500 flex items-center">{c.items.length} <ChevronRight size={16} /></span>
            </div>
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2">
              {c.items.map((item, i) => card(item, i + ci))}
            </div>
          </section>
        ))
      )}
    </div>
  )
}
